import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate'

import { type AdverseEventsFormValues } from '#app/routes/mocra+/_form+/adverse-events/form.ts'

import { type IntakeDraft } from '#app/routes/mocra+/_form+/generate/intake-loader.ts'
import { type TemplateId } from '#app/routes/mocra+/_form+/generate/summary.controller.ts'
import { type PrimaryContactsFormValues } from '#app/routes/mocra+/_form+/primary-contacts.tsx'

type DocxTemplateDefinition = {
	readonly id: TemplateId
	readonly filename: string
	readonly templatePath: string
	readonly buildReplacements: (
		draft: IntakeDraft,
		generatedAt: Date,
	) => Record<string, string>
}

type DocxTemplateMap = Map<TemplateId, DocxTemplateDefinition>

const DOCUMENT_XML_PATH = 'word/document.xml'

const DOCX_TEMPLATES: readonly DocxTemplateDefinition[] = [
	{
		id: 'adverse-event',
		filename: '§605-adverse-event.docx',
		templatePath: '/mocra/sop-templates/§605-adverse-event.docx',
		buildReplacements: (draft, generatedAt) =>
			buildAdverseEventReplacements(draft, generatedAt),
	},
] as const

const DOCX_TEMPLATE_MAP: DocxTemplateMap = new Map(
	DOCX_TEMPLATES.map((template) => [template.id, template]),
)

export function hasDocxTemplate(id: TemplateId): boolean {
	return DOCX_TEMPLATE_MAP.has(id)
}

export async function renderDocxTemplate(
	id: TemplateId,
	draft: IntakeDraft,
	generatedAt: Date,
) {
	const definition = DOCX_TEMPLATE_MAP.get(id)
	if (!definition) {
		throw new Error(`No DOCX template registered for ${id}`)
	}
	if (typeof window === 'undefined') {
		throw new Error('DOCX generation is only supported in the browser.')
	}

	const response = await fetch(definition.templatePath)
	if (!response.ok) {
		throw new Error(
			`Failed to load DOCX template from ${definition.templatePath}`,
		)
	}

	const buffer = await response.arrayBuffer()
	const archive = unzipSync(new Uint8Array(buffer))
	const documentXml = archive[DOCUMENT_XML_PATH]
	if (!documentXml) {
		throw new Error(
			`DOCX template is missing ${DOCUMENT_XML_PATH} entry. Unable to generate document.`,
		)
	}

	const replacements = definition.buildReplacements(draft, generatedAt)
	const updatedXml = applyReplacements(strFromU8(documentXml), replacements)
	archive[DOCUMENT_XML_PATH] = strToU8(updatedXml)

	const zipped = zipSync(
		Object.fromEntries(
			Object.entries(archive).map(([name, data]) => [name, data]),
		),
	)

	const blob = new Blob([Uint8Array.from(zipped)], {
		type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	})

	return { filename: definition.filename, blob }
}

function applyReplacements(xml: string, replacements: Record<string, string>) {
	return Object.entries(replacements).reduce(
		(contents, [placeholder, value]) =>
			contents.replaceAll(placeholder, escapeXml(value)),
		xml,
	)
}

function escapeXml(value: string) {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&apos;')
}

function buildAdverseEventReplacements(
	draft: IntakeDraft,
	generatedAt: Date,
): Record<string, string> {
	const company = draft.company
	const primaryContacts = draft.primaryContacts
	const adverseEvents = draft.adverseEvents

	const companyLegalName = normalize(company?.companyLegalName)
	const responsibleLabelName = normalize(company?.responsiblePersonLabelName)
	const complianceEmail = normalize(company?.complianceContactEmail)

	const regulatoryContact = resolvePrimaryContact(
		primaryContacts,
		'regulatoryLeadId',
	)

	const regulatoryName = normalize(regulatoryContact?.name)
	const regulatoryEmail = normalize(regulatoryContact?.email)
	const regulatoryPhone = normalize(regulatoryContact?.phone)

	const contactEmail = regulatoryEmail ?? complianceEmail
	const contactSummary = formatContactSummary(
		regulatoryName,
		contactEmail,
		regulatoryPhone,
	)

	const generatedDateLabel = formatDate(generatedAt)
	const nextReviewDate = formatDate(addYears(generatedAt, 1))

	const acknowledgementWindow = computeAcknowledgementWindow(adverseEvents)

	return {
		'[Company Name]': fallback(companyLegalName),
		'[Company]': fallback(responsibleLabelName ?? companyLegalName),
		'[company]': fallback(
			slugify(responsibleLabelName ?? companyLegalName),
			'your-company',
		),
		'[Regulatory Lead Name]': fallback(regulatoryName),
		'[Phone Number]': fallback(regulatoryPhone),
		'[Email Address]': fallback(regulatoryEmail ?? contactEmail),
		'[contact info]': fallback(contactEmail),
		'[contact information]': fallback(contactSummary),
		'[Insert Date]': generatedDateLabel,
		'[Annual Review Required]': nextReviewDate,
		'[X]': fallback(acknowledgementWindow),
		'[YEAR]': `${generatedAt.getFullYear()}`,
		'[Sequential Number]': '0001',
	}
}

function computeAcknowledgementWindow(adverseEvents?: AdverseEventsFormValues) {
	const acknowledgement = adverseEvents?.acknowledgement
	if (!acknowledgement) return null
	const window = acknowledgement.window
	switch (window) {
		case 'same_day':
			return '24'
		case 'within_1_business_day':
			return '24'
		case 'within_2_business_days':
			return '48'
		case 'within_3_business_days':
			return '72'
		case 'custom':
			return normalize(acknowledgement.customWindow)
		default:
			return null
	}
}

function resolvePrimaryContact(
	data: PrimaryContactsFormValues | undefined,
	key: keyof PrimaryContactsFormValues['assignments'],
) {
	if (!data) return null
	const contactId = data.assignments[key]
	if (!contactId) return null
	return data.contacts.find((contact) => contact.id === contactId) ?? null
}

function formatContactSummary(
	name: string | null,
	email: string | null,
	phone: string | null,
) {
	const parts = [name, phone, email].filter(
		(part): part is string => typeof part === 'string' && part.length > 0,
	)
	return parts.join(' • ') || null
}

function normalize(value: string | null | undefined) {
	if (!value) return null
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed.replace(/\s{2,}/g, ' ') : null
}

function fallback(
	value: string | null | undefined,
	defaultValue = 'Not provided yet',
) {
	return value ?? defaultValue
}

function slugify(value: string | null | undefined) {
	const normalized = normalize(value)
	if (!normalized) return null
	const slug = normalized
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
	return slug || null
}

function formatDate(date: Date) {
	return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(date)
}

function addYears(date: Date, years: number) {
	const next = new Date(date)
	next.setFullYear(date.getFullYear() + years)
	return next
}

export const __docxTestables = {
	applyReplacements,
	buildAdverseEventReplacements,
	computeAcknowledgementWindow,
	escapeXml,
	formatContactSummary,
	fallback,
	normalize,
	slugify,
}
